import axiosInstance from 'api/axiosConfig'
import handleApiError from 'api/utils/handleApiError'
import { Filters } from 'types/general'

import { ENDPOINTS } from '../endpoints'

export type SearchAlertStatus = 'pending_confirmation' | 'active' | 'paused'

export type SearchAlert = {
  id: number
  email: string
  criteria: Filters | Record<string, unknown> | null
  summaryLabel: string
  status: SearchAlertStatus
  confirmedAt: string | null
  lastMatchedAt: string | null
  newMatchesCount: number
  createdAt: string | null
  updatedAt: string | null
}

export type CreateSearchAlertPayload = {
  email?: string
  criteria: Filters | Record<string, unknown>
  summaryLabel: string
}

export type CreateSearchAlertResult = {
  id: number
  email: string
  status: SearchAlertStatus
  emailSent: boolean
}

const useSearchAlertsService = () => {
  const createAlert = async (
    payload: CreateSearchAlertPayload
  ): Promise<CreateSearchAlertResult | null> => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.SEARCH_ALERTS.BASE, payload)
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to create alert')
      }
      return response.data.data as CreateSearchAlertResult
    } catch (err) {
      handleApiError('Failed to create search alert', err)
      return null
    }
  }

  const loadMyAlerts = async (): Promise<SearchAlert[]> => {
    try {
      const response = await axiosInstance.get(ENDPOINTS.SEARCH_ALERTS.BASE)
      if (!response.data?.success) {
        throw new Error()
      }
      return (response.data.data ?? []) as SearchAlert[]
    } catch (err) {
      handleApiError('Failed to load search alerts', err)
      return []
    }
  }

  const confirmAlert = async (token: string): Promise<SearchAlert | null> => {
    try {
      const response = await axiosInstance.get(ENDPOINTS.SEARCH_ALERTS.BASE, {
        params: { token },
      })
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Invalid confirmation link')
      }
      return response.data.data as SearchAlert
    } catch (err) {
      handleApiError('Failed to confirm search alert', err)
      return null
    }
  }

  const updateAlert = async (payload: {
    id: number
    status?: 'active' | 'paused'
    criteria?: Filters | Record<string, unknown>
    summaryLabel?: string
    newMatchesCount?: number
  }): Promise<SearchAlert | null> => {
    try {
      const response = await axiosInstance.patch(ENDPOINTS.SEARCH_ALERTS.BASE, payload)
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to update alert')
      }
      return response.data.data as SearchAlert
    } catch (err) {
      handleApiError('Failed to update search alert', err)
      return null
    }
  }

  const deleteAlert = async (id: number): Promise<boolean> => {
    try {
      const response = await axiosInstance.delete(ENDPOINTS.SEARCH_ALERTS.BASE, {
        data: { id },
      })
      return response.data?.success === true
    } catch (err) {
      handleApiError('Failed to delete search alert', err)
      return false
    }
  }

  return {
    createAlert,
    loadMyAlerts,
    confirmAlert,
    updateAlert,
    deleteAlert,
  }
}

export default useSearchAlertsService
